import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { AdminService } from '../../services/AdminService/admin';
import { CompanyService } from '../../services/CompanyService/company';
import { AuthService } from '../../services/AuthService/auth';
import { CountryTermRuleResponse } from '../../models/country-config.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';
import { findCountry } from '../../shared/countries';

export interface FieldRuleGroup {
  eventType: string;
  rules: CountryTermRuleResponse[];
}

/** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
function buildPageWindow(current: number, total: number): number[] {
  const pages: number[] = [];
  const add = (p: number) => {
    if (pages[pages.length - 1] !== p) {
      pages.push(p);
    }
  };

  add(0);
  for (let p = current - 1; p <= current + 1; p++) {
    if (p > 0 && p < total - 1) {
      if (p > pages[pages.length - 1] + 1) {
        add(-1);
      }
      add(p);
    }
  }
  if (total - 1 > pages[pages.length - 1] + 1) {
    add(-1);
  }
  if (total > 1) {
    add(total - 1);
  }
  return pages;
}

@Component({
  selector: 'app-country-schema',
  imports: [CommonModule, FormsModule, ErrorBanner, EmptyState, PageSizeSelect],
  templateUrl: './country-schema.html',
  styleUrl: './country-schema.scss',
})
export class CountrySchema implements OnInit {
  // All rules across every country, fetched once — the country toggle just filters this
  // client-side rather than round-tripping, and it's also how we discover which countries
  // actually have a configured schema instead of hardcoding a list.
  allRules = signal<CountryTermRuleResponse[]>([]);
  selectedCountry = signal('');
  fieldSearch = '';

  isLoading = signal(false);
  errorMessage = signal('');

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);

  // Each event-type group paginates independently, keyed by eventType.
  private groupPages = new Map<string, number>();

  constructor(
    private admin: AdminService,
    private companyService: CompanyService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    // Company roles are always tied to a companyId (see role-families.ts); platform roles never
    // are. Preselect their own country so exporters land on the schema they most likely need.
    if (!this.auth.getSession()?.companyId) {
      this.load();
      return;
    }

    this.companyService.me().subscribe({
      next: (res) => {
        this.selectedCountry.set(res.data.countryCode);
        this.load();
      },
      error: () => this.load(),
    });
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.groupPages.clear();

    this.admin.listTermRules().subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.allRules.set(res.data);
        const countries = new Set(res.data.map((r) => r.countryCode));
        if ((!this.selectedCountry() || !countries.has(this.selectedCountry())) && res.data.length > 0) {
          this.selectedCountry.set(res.data[0].countryCode);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load country schemas.'));
      },
    });
  }

  get availableCountries(): string[] {
    return Array.from(new Set(this.allRules().map((r) => r.countryCode))).sort();
  }

  countryLabel(code: string): string {
    const country = findCountry(code);
    return country ? `${code} · ${country.name}` : code;
  }

  selectCountry(code: string): void {
    this.selectedCountry.set(code);
    this.groupPages.clear();
  }

  onSearchChange(): void {
    this.groupPages.clear();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.groupPages.clear();
  }

  get groupedRules(): FieldRuleGroup[] {
    const term = this.fieldSearch.trim().toLowerCase();
    const matching = this.allRules().filter((r) => {
      if (r.countryCode !== this.selectedCountry()) {
        return false;
      }
      if (!term) {
        return true;
      }
      return (
        r.term.toLowerCase().includes(term) ||
        (r.localName ?? '').toLowerCase().includes(term)
      );
    });

    const groups = new Map<string, CountryTermRuleResponse[]>();
    for (const rule of matching) {
      const list = groups.get(rule.eventType) ?? [];
      list.push(rule);
      groups.set(rule.eventType, list);
    }

    const entries: FieldRuleGroup[] = Array.from(groups.entries()).map(([eventType, rules]) => ({
      eventType,
      rules: [...rules].sort((a, b) => a.term.localeCompare(b.term)),
    }));

    entries.sort((a, b) => {
      if (a.eventType === '*') return -1;
      if (b.eventType === '*') return 1;
      return a.eventType.localeCompare(b.eventType);
    });

    return entries;
  }

  eventTypeLabel(eventType: string): string {
    return eventType === '*' ? 'Every event type' : eventType;
  }

  groupPage(group: FieldRuleGroup): number {
    return this.groupPages.get(group.eventType) ?? 0;
  }

  groupTotalPages(group: FieldRuleGroup): number {
    return Math.max(1, Math.ceil(group.rules.length / this.pageSize()));
  }

  pagedGroupRules(group: FieldRuleGroup): CountryTermRuleResponse[] {
    const start = this.groupPage(group) * this.pageSize();
    return group.rules.slice(start, start + this.pageSize());
  }

  groupRangeStart(group: FieldRuleGroup): number {
    return group.rules.length === 0 ? 0 : this.groupPage(group) * this.pageSize() + 1;
  }

  groupRangeEnd(group: FieldRuleGroup): number {
    return Math.min((this.groupPage(group) + 1) * this.pageSize(), group.rules.length);
  }

  groupPageWindow(group: FieldRuleGroup): number[] {
    return buildPageWindow(this.groupPage(group), this.groupTotalPages(group));
  }

  goToGroupPage(group: FieldRuleGroup, page: number): void {
    const total = this.groupTotalPages(group);
    if (page < 0 || page >= total || page === this.groupPage(group)) {
      return;
    }
    this.groupPages.set(group.eventType, page);
  }
}
